import { QueryClient } from '@tanstack/react-query';
import { AnyRoute, createRouter, redirect } from '@tanstack/react-router';
import { AxiosError } from 'axios';

import { MapAxiosErrorToRocketError } from '../api/errors';

export interface AppRouterContext {
  queryClient: QueryClient;
  ensureQueryData: QueryClient['ensureQueryData'];
}

interface CreateAppRouterOptions<TRouteTree extends AnyRoute> {
  routeTree: TRouteTree;
  queryClient: QueryClient;
  mapAxiosErrorToRocketError: MapAxiosErrorToRocketError;
  getRedirectPath: (errorKey: string) => string | null;
  defaultErrorRedirect: string;
}

export function createAppRouter<TRouteTree extends AnyRoute>({
  routeTree,
  queryClient,
  mapAxiosErrorToRocketError,
  getRedirectPath,
  defaultErrorRedirect,
}: CreateAppRouterOptions<TRouteTree>) {
  return createRouter({
    routeTree,
    context: {
      queryClient,
      ensureQueryData: (async options => {
        try {
          return await queryClient.ensureQueryData(options);
        } catch (error) {
          if (error instanceof AxiosError) {
            const rocketError = mapAxiosErrorToRocketError(error, 'query', options.meta);
            const redirectPath = rocketError ? getRedirectPath(rocketError.message) : null;

            throw redirect({
              to: redirectPath ?? defaultErrorRedirect,
              replace: true,
            });
          }
          throw error;
        }
      }) as QueryClient['ensureQueryData'],
    },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}
